import React, { useEffect, useState, useMemo } from 'react'
import { Button } from '../../components/ui'
import { Select } from '../../components/ui/Select'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import { AlertCircle, Plus, Calendar, AlertTriangle, XCircle } from 'lucide-react'
import AddIncidentModal from '../../components/tpsm/AddIncidentModal'
import { pageScope } from '../../lib/cssScope'

type Incident = {
  id: string
  supplierId: string
  supplier?: {
    id: string
    name: string
    email?: string
  }
  type: string
  severity: string
  description?: string
  occurredAt: string
  createdAt: string
}

function severityPillClass(severity: string, styles: typeof listStyles): string {
  switch (severity) {
    case 'CRITICAL':
      return `${styles.riskLevelPill} ${styles.riskLevelCritical}`
    case 'HIGH':
      return `${styles.riskLevelPill} ${styles.riskLevelHigh}`
    case 'MEDIUM':
      return `${styles.riskLevelPill} ${styles.riskLevelMedium}`
    case 'LOW':
      return `${styles.riskLevelPill} ${styles.riskLevelLow}`
    default:
      return `${styles.riskLevelPill} ${styles.riskLevelVeryLow}`
  }
}

const TPSMIncidents: React.FC = () => {
  const { token } = useAuth()
  const { t } = useI18n()
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    severity: 'all',
    type: 'all'
  })
  const [showAddModal, setShowAddModal] = useState(false)

  useEffect(() => {
    fetchIncidents()
    fetchSummary()
  }, [token, filters])

  const fetchIncidents = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.severity !== 'all') params.append('severity', filters.severity)
      if (filters.type !== 'all') params.append('type', filters.type)

      const res = await fetch(`${API_ENDPOINTS.TPSM_INCIDENTS}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      setIncidents(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setError(e.message || 'Failed to load incidents')
    } finally {
      setLoading(false)
    }
  }

  const fetchSummary = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.TPSM_INCIDENTS_SUMMARY, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      setSummary(data)
    } catch {
      // Silently fail
    }
  }

  const filteredIncidents = incidents.filter(i => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        i.supplier?.name?.toLowerCase().includes(query) ||
        i.type.toLowerCase().includes(query) ||
        i.description?.toLowerCase().includes(query) ||
        i.severity.toLowerCase().includes(query)
      )
    }
    return true
  })

  const sortedIncidents = [...filteredIncidents].sort((a, b) =>
    new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
  )

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <XCircle className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(220 38 38)' }} />
      case 'HIGH':
        return <AlertTriangle className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(234 88 12)' }} />
      case 'MEDIUM':
        return <AlertCircle className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(202 138 4)' }} />
      default:
        return <AlertCircle className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(107 114 128)' }} />
    }
  }

  const getTypeLabel = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const types = useMemo(() => {
    const incidentTypes = Array.from(new Set(incidents.map(i => i.type))).sort()
    if (incidentTypes.length === 0) {
      return ['DATA_BREACH', 'SECURITY_INCIDENT', 'COMPLIANCE_VIOLATION', 'SERVICE_DISRUPTION', 'VULNERABILITY_DISCLOSURE']
    }
    return incidentTypes
  }, [incidents])
  const severities = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']

  return (
    <div className={`${pageScope('tpsm-incidents')} ${listStyles.pageStack}`}>
      <GovernanceListingSection title={t('tpsm.incidents.metricsTitle', 'Incident metrics')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.incidents.total', 'Total Incidents')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{summary?.total ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.incidents.critical', 'Critical')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{summary?.bySeverity?.CRITICAL ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileOrange}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.incidents.high', 'High')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textOrange}`}>{summary?.bySeverity?.HIGH ?? 0}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.incidents.medium', 'Medium')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{summary?.bySeverity?.MEDIUM ?? 0}</p>
          </div>
        </div>
      </GovernanceListingSection>

      {summary?.byType && Object.keys(summary.byType).length > 0 && (
        <GovernanceListingSection title={t('tpsm.incidents.byType', 'Incidents by Type')}>
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            {Object.entries(summary.byType).map(([type, count]: [string, any]) => (
              <div key={type} className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
                <p className={listStyles.metricTileLabel}>{getTypeLabel(type)}</p>
                <p className={`${listStyles.metricTileValue} ${listStyles.cellStrong}`}>{count}</p>
              </div>
            ))}
          </div>
        </GovernanceListingSection>
      )}

      <GovernanceListingSection
        title={t('tpsm.incidents.filters', 'Filters')}
        toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                <SearchField
                  label={t('tpsm.incidents.searchLabel', 'Search')}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t('tpsm.incidents.searchPlaceholder', 'Search incidents...')}
                  minWidth="14rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.incidents.filterSeverity', 'Severity')}
                  options={[
                    { value: 'all', label: t('tpsm.incidents.allSeverities', 'All Severities') },
                    ...severities.map((sev) => ({ value: sev, label: sev }))
                  ]}
                  value={filters.severity}
                  onChange={(v) => v != null && setFilters({ ...filters, severity: v })}
                  minWidth="11rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.incidents.filterType', 'Type')}
                  options={[
                    { value: 'all', label: t('tpsm.incidents.allTypes', 'All Types') },
                    ...types.map((type) => ({ value: type, label: getTypeLabel(type) }))
                  ]}
                  value={filters.type}
                  onChange={(v) => v != null && setFilters({ ...filters, type: v })}
                  minWidth="11rem"
                />
              </div>
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  color="primary"
                  onClick={() => setShowAddModal(true)}
                  icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
                >
                  {t('tpsm.incidents.newIncident', 'New Incident')}
                </Button>
              </div>
            </div>
          </div>
        }
      >
        {null}
      </GovernanceListingSection>

      <GovernanceListingSection
        title={`${t('tpsm.incidents.title', 'Incidents with Suppliers')} (${sortedIncidents.length})`}
      >
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.table.severity', 'Severity')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.table.supplier', 'Supplier')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.table.type', 'Type')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.table.description', 'Description')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.occurredAt', 'Occurred')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('tpsm.incidents.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableLoadingRow colSpan={6}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              ) : error ? (
                <TableErrorRow colSpan={6}>{error}</TableErrorRow>
              ) : sortedIncidents.length === 0 ? (
                <TableEmptyRow colSpan={6}>
                  {t('tpsm.incidents.noIncidents', 'No incidents found')}
                </TableEmptyRow>
              ) : (
                sortedIncidents.map((incident) => (
                  <tr key={incident.id} className={listStyles.row}>
                    <td className={listStyles.td}>
                      <span className={listStyles.actionsGap}>
                        {getSeverityIcon(incident.severity)}
                        <span className={severityPillClass(incident.severity, listStyles)}>{incident.severity}</span>
                      </span>
                    </td>
                    <td className={`${listStyles.td} ${listStyles.cellStrong}`}>
                      {incident.supplier?.name || 'Unknown Supplier'}
                      {incident.supplier?.email && (
                        <div className={listStyles.cellMuted}>{incident.supplier.email}</div>
                      )}
                    </td>
                    <td className={listStyles.td}>{getTypeLabel(incident.type)}</td>
                    <td className={listStyles.td}>
                      {incident.description ? (
                        <span className={listStyles.lineClamp2}>{incident.description}</span>
                      ) : (
                        <span className={listStyles.cellMuted}>—</span>
                      )}
                    </td>
                    <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                      <span className={listStyles.actionsGap}>
                        <Calendar className={listStyles.badgeIcon} aria-hidden />
                        {new Date(incident.occurredAt).toLocaleString()}
                      </span>
                    </td>
                    <td className={listStyles.td}>
                      <div className={listStyles.actionsGap}>
                        <Button type="button" color="secondary" size="small">
                          {t('tpsm.incidents.view', 'View')}
                        </Button>
                        <Button type="button" color="secondary" size="small">
                          {t('tpsm.incidents.edit', 'Edit')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>

      <AddIncidentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchIncidents()
          fetchSummary()
          setShowAddModal(false)
        }}
      />
    </div>
  )
}

export default TPSMIncidents
